/**
 * Hydrates product-card color variations from Storefront GraphQL (linked products).
 * Requires #jkl-storefront-card-variations JSON (theme.liquid) and product-card[data-variations-client].
 */
(function () {
  const CONFIG_EL = document.getElementById("jkl-storefront-card-variations");
  if (!CONFIG_EL) return;

  let config;
  try {
    config = JSON.parse(CONFIG_EL.textContent);
  } catch {
    return;
  }
  if (!config.token || !config.domain) return;

  const API_URL =
    "https://" + config.domain + "/api/" + config.apiVersion + "/graphql.json";

  const lang =
    typeof config.language === "string" && config.language.trim()
      ? config.language.trim()
      : "";
  const inContextArgs = lang
    ? "country: " + config.country + ", language: " + lang
    : "country: " + config.country;

  const variationProductFields = `
                id
                handle
                title
                availableForSale
                onlineStoreUrl
                featuredImage {
                  url(transform: { maxWidth: 1200, maxHeight: 1200 })
                  altText
                  width
                  height
                }
                images(first: 2) {
                  edges {
                    node {
                      url(transform: { maxWidth: 1200, maxHeight: 1200 })
                      altText
                      width
                      height
                    }
                  }
                }
                hexColor: metafield(namespace: "custom", key: "hex_color") { value }
                codColore: metafield(namespace: "custom", key: "cod_colore") { value }
                ucbColore: metafield(namespace: "custom", key: "ucb_colore") { value }
                priceRange {
                  minVariantPrice { amount currencyCode }
                }
                compareAtPriceRange {
                  minVariantPrice { amount currencyCode }
                }
  `;

  /** Prefer GID lookup: translated URL handles (Markets / Translate & Adapt) differ from canonical EN handles. */
  const QUERY_BY_PRODUCT_ID =
    "\n    query JklCardVariationRefs($id: ID!) @inContext(" +
    inContextArgs +
    ') {\n      node(id: $id) {\n        ... on Product {\n          id\n          handle\n          metafield(namespace: "custom", key: "variation_products") {\n            references(first: 30) {\n              nodes {\n                ... on Product {\n' +
    variationProductFields +
    "\n                }\n              }\n            }\n          }\n        }\n      }\n    }\n  ";

  const QUERY_BY_HANDLE =
    "\n    query JklCardVariationRefs($handle: String!) @inContext(" +
    inContextArgs +
    ') {\n      product(handle: $handle) {\n        handle\n        metafield(namespace: "custom", key: "variation_products") {\n          references(first: 30) {\n            nodes {\n              ... on Product {\n' +
    variationProductFields +
    "\n              }\n            }\n          }\n        }\n      }\n    }\n  ";

  const cache = new Map();

  function money(amountStr, currencyCode) {
    const amount = parseFloat(amountStr, 10);
    if (Number.isNaN(amount)) return "";
    const opts = {
      style: "currency",
      currency: currencyCode || config.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };
    if (config.currencyCodeEnabled === false) {
      opts.currencyDisplay = "narrowSymbol";
    }
    try {
      return new Intl.NumberFormat(config.locale || undefined, opts).format(
        amount,
      );
    } catch {
      return amountStr;
    }
  }

  function formatColorCount(n) {
    const ph = String(config.colorCountPlaceholder || "999888");
    const tpl = config.colorCountTemplate || "";
    if (tpl.indexOf(ph) !== -1) return tpl.split(ph).join(String(n));
    return String(n) + " colors";
  }

  function buildPriceListHtml(node) {
    const price = parseFloat(
      node.priceRange?.minVariantPrice?.amount || "0",
      10,
    );
    const cur =
      node.priceRange?.minVariantPrice?.currencyCode || config.currency;
    const cmpNode = node.compareAtPriceRange?.minVariantPrice;
    const cmpAmount =
      cmpNode?.amount != null ? parseFloat(cmpNode.amount, 10) : 0;
    const cmpCur = cmpNode?.currencyCode || cur;
    const onSale = cmpAmount > price && cmpAmount > 0;

    const saleCls = onSale ? "text-on-sale" : "text-subdued";
    const compareCls = "text-subdued line-through";

    let inner =
      '<sale-price class="' +
      saleCls +
      '"><span class="sr-only">' +
      escapeHtml(config.priceSaleLabel || "") +
      "</span>" +
      escapeHtml(money(String(price), cur)) +
      "</sale-price>";

    if (onSale) {
      inner +=
        '<compare-at-price class="' +
        compareCls +
        '"><span class="sr-only">' +
        escapeHtml(config.priceRegularLabel || "") +
        "</span>" +
        escapeHtml(money(String(cmpAmount), cmpCur)) +
        "</compare-at-price>";
    }

    return '<price-list class="price-list">' + inner + "</price-list>";
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function hexBrightness(hex) {
    if (!hex || typeof hex !== "string" || !hex.startsWith("#")) return 0;
    const h = hex.slice(1);
    const full =
      h.length === 3
        ? h
            .split("")
            .map(function (c) {
              return c + c;
            })
            .join("")
        : h;
    if (full.length !== 6) return 0;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000;
  }

  function swatchLabel(node) {
    const u = node.ucbColore?.value;
    const c = node.codColore?.value;
    return String(u || c || node.title || "").trim();
  }

  /** Short text for sr-only; avoid repeating the full product title under every swatch. */
  function swatchSrOnly(node) {
    const u = node.ucbColore?.value;
    const c = node.codColore?.value;
    const t = String(u || c || "").trim();
    if (t) return t;
    const h = node.handle;
    if (h) return h.replace(/-/g, " ");
    return "Color";
  }

  function productUrl(node) {
    if (node.onlineStoreUrl) return node.onlineStoreUrl;
    return (
      (config.storeRoot || "/") + "products/" + encodeURIComponent(node.handle)
    );
  }

  function primaryImageUrl(node) {
    return node.featuredImage?.url || "";
  }

  function secondaryImage(node) {
    const edges = node.images?.edges || [];
    if (edges.length < 2) return null;
    return edges[1]?.node || null;
  }

  function normalizeProductFromData(data) {
    if (!data) return null;
    if (data.product) return data.product;
    const n = data.node;
    if (n && (n.metafield !== undefined || n.id || n.handle)) return n;
    return null;
  }

  async function fetchVariations(cacheKey, query, variables) {
    if (cache.has(cacheKey)) return cache.get(cacheKey);
    const p = (async function () {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": config.token,
        },
        body: JSON.stringify({
          query: query,
          variables: variables,
        }),
      });
      const json = await res.json();
      if (json.errors && json.errors.length)
        throw new Error(json.errors[0].message);
      return json.data;
    })();
    cache.set(cacheKey, p);
    return p;
  }

  function renderCard(card) {
    if (card.getAttribute("data-variations-hydrated") === "true") return;
    const mount = card.querySelector("[data-variations-mount]");
    if (!mount) return;

    const productIdRaw = (
      card.getAttribute("data-variations-product-id") || ""
    ).trim();
    const handle = (card.getAttribute("handle") || "").trim();
    const rootProductUrl = (
      card.getAttribute("data-variations-root-product-url") || ""
    ).trim();

    if (!productIdRaw && !handle) return;

    card.setAttribute("data-variations-hydrated", "true");

    const showAlways =
      card.getAttribute("data-show-variation-picker-always") === "true";
    const rootHandle = handle;
    const rootGid = productIdRaw ? "gid://shopify/Product/" + productIdRaw : "";

    const cacheKey = productIdRaw ? "id:" + productIdRaw : "handle:" + handle;
    const gqlQuery = productIdRaw ? QUERY_BY_PRODUCT_ID : QUERY_BY_HANDLE;
    const variables = productIdRaw ? { id: rootGid } : { handle: handle };

    fetchVariations(cacheKey, gqlQuery, variables)
      .then(function (data) {
        const product = normalizeProductFromData(data);
        const refs =
          product &&
          product.metafield &&
          product.metafield.references &&
          product.metafield.references.nodes
            ? product.metafield.references.nodes
            : [];

        const available = refs.filter(function (n) {
          return n && n.availableForSale && n.onlineStoreUrl;
        });

        const ordered = [];
        for (let i = 0; i < Math.min(4, refs.length); i++) {
          const n = refs[i];
          if (n && n.availableForSale && n.onlineStoreUrl) ordered.push(n);
        }

        if (!available.length) {
          mount.remove();
          return;
        }

        const parts = [];
        let countHtml = "";
        if (!showAlways) {
          countHtml =
            '<p class="product-card__variation-count">' +
            escapeHtml(formatColorCount(available.length)) +
            "</p>";
        }

        for (let i = 0; i < ordered.length; i++) {
          const node = ordered[i];
          const vhandle = node.handle;
          const title = node.title || "";
          const primaryUrl = primaryImageUrl(node);
          const sec = secondaryImage(node);
          const secUrl = sec?.url || "";
          const fi = node.featuredImage;
          const cod = node.codColore?.value || "";
          const hex = node.hexColor?.value || "";
          const selected = rootGid
            ? node.id === rootGid
            : vhandle === rootHandle;
          const href = productUrl(node);

          const swatchStyle = hex
            ? "--swatch-background: linear-gradient(to right, " +
              hex +
              ", " +
              hex +
              ");"
            : primaryUrl
              ? "--swatch-background: url(" + primaryUrl + ");"
              : "";

          const bright = hex && hexBrightness(hex) > 200;
          const whiteLabels = config.whiteLabels || [];
          const downLabel = String(swatchLabel(node)).toLowerCase().trim();
          const ringInset =
            whiteLabels.indexOf(downLabel) !== -1 ? "ring-inset" : "";
          const rect =
            config.colorSwatchStyle === "rectangle"
              ? "color-swatch--rectangle"
              : "";
          const round =
            config.colorSwatchStyle === "round" ? "rounded-full" : "";
          const borderBright = bright ? "color-swatch-border" : "";

          parts.push(
            '<template data-price-for="' +
              escapeHtml(vhandle) +
              '">' +
              buildPriceListHtml(node) +
              "</template>",
          );

          parts.push(
            '<span data-variation-handle="' +
              escapeHtml(vhandle) +
              '" data-variation-url="' +
              escapeHtml(href) +
              '" data-variation-title="' +
              escapeHtml(title) +
              '" data-variation-color="' +
              escapeHtml(cod) +
              '" data-variation-image="' +
              escapeHtml(primaryUrl) +
              '" data-variation-image-alt="' +
              escapeHtml(fi?.altText || title) +
              '" data-variation-image-width="' +
              escapeHtml(String(fi?.width || "")) +
              '" data-variation-image-height="' +
              escapeHtml(String(fi?.height || "")) +
              '" data-variation-secondary-image="' +
              escapeHtml(secUrl) +
              '" data-variation-secondary-image-alt="' +
              escapeHtml(sec?.altText || title) +
              '" data-variation-secondary-image-width="' +
              escapeHtml(String(sec?.width || "")) +
              '" data-variation-secondary-image-height="' +
              escapeHtml(String(sec?.height || "")) +
              '"><div class="color-swatch-container"><a class="color-swatch ' +
              rect +
              " color-swatch--sm " +
              ringInset +
              " " +
              round +
              " " +
              borderBright +
              (selected ? " is-selected" : "") +
              '" href="' +
              escapeHtml(href) +
              '" style="' +
              escapeHtml(swatchStyle) +
              '"' +
              (cod ? ' data-color="' + escapeHtml(cod) + '"' : "") +
              '><span class="sr-only">' +
              escapeHtml(swatchSrOnly(node)) +
              "</span></a></div></span>",
          );
        }

        const hoverClass = showAlways
          ? ""
          : " product-card__variation-picker--hover-only";
        const secondColClass = showAlways
          ? ""
          : "product-card__variation-picker--hover-only";

        let moreCol = "";
        if (available.length > 4) {
          const rootUrl =
            rootProductUrl ||
            (config.storeRoot || "/") +
              "products/" +
              encodeURIComponent(rootHandle);
          moreCol =
            '<div class="' +
            secondColClass +
            '"><a href="' +
            escapeHtml(rootUrl) +
            '" data-instant target="_blank" class="product-card__variation-more"><span>+' +
            String(available.length - 4) +
            "</span></a></div>";
        }

        mount.outerHTML =
          countHtml +
          '<div class="product-card__variants"><div class="product-card__variation-picker' +
          hoverClass +
          '"><div class="variant-picker"><fieldset class="variant-picker__option"><div class="variant-picker__option-values wrap gap-2">' +
          parts.join("") +
          "</div></fieldset></div></div>" +
          moreCol +
          "</div>";
      })
      .catch(function () {
        card.removeAttribute("data-variations-hydrated");
        mount.remove();
      });
  }

  function boot() {
    document
      .querySelectorAll("product-card[data-variations-client]")
      .forEach(function (card) {
        if (card.getAttribute("data-variations-hydrated") === "true") return;
        renderCard(card);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("facet:updated", function (e) {
    if (e.detail && e.detail.success === false) return;
    requestAnimationFrame(boot);
  });
  // Load-more / ?vp= replay appends cards without section reload (see snippets/jkl-pagination.liquid).
  document.addEventListener("collection:load-more", function () {
    requestAnimationFrame(boot);
  });
})();
